import type { Cell, NoteBlock, Tone } from '@/kova/spec/types'

import { ApiError } from '@/lib/api/errors'
import { exactMinor, isCurrencyCode } from '@/lib/money/money'

import { readAtStamp } from './provenance'

/**
 * Reading the wire, fail-closed — the part every adapter was writing again.
 *
 * # Why this exists now and not five modules ago
 *
 * `aml.ts`, `storefront.ts`, `onboarding.ts` and `providers.ts` each carry
 * their own copy of `asRecord`, `readInt`, `readInstant`, `summarise` and the
 * rest. That was defensible while there were two: the rules are subtle enough
 * that a shared helper somebody tunes for one screen would quietly change
 * another, and the modules were reviewed one at a time.
 *
 * It stops being defensible at six. Every copy is the same forty lines, and the
 * whole point of them is that ONE reading of "absent, unusable and present are
 * three different facts" governs the console — a sixth transcription is a sixth
 * chance for `readDuration` to stop rejecting a negative, and that failure is
 * silent and renders as `0s`.
 *
 * # It is additive on purpose
 *
 * The four modules above are NOT migrated here. This module is used by the two
 * that arrive with it, so nothing already reviewed changes behaviour and the
 * diff stays inside one tranche; moving the others across is a mechanical
 * change that can be made on its own with the suite green. What must not happen
 * is a seventh copy.
 *
 * # None of these ever throws
 *
 * A loader resolves for every answer the backend gives, including "I could not
 * read that". Every function here returns a value describing what it found, and
 * the caller decides what the screen says about it.
 */

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

export function asRecord(raw: unknown): Record<string, unknown> | null {
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : null
}

/**
 * `Array.isArray` narrows `unknown` to `any[]`, which then spreads `any`
 * through everything it touches. Landing back on `readonly unknown[]` keeps
 * every element a thing the parse has to prove something about.
 */
export function asArray(raw: unknown): readonly unknown[] | null {
  return Array.isArray(raw) ? (raw as readonly unknown[]) : null
}

export function readText(o: Record<string, unknown>, key: string): string | null {
  const raw = o[key]
  return typeof raw === 'string' && raw !== '' ? raw : null
}

export function readBool(o: Record<string, unknown>, key: string): boolean | null {
  const raw = o[key]
  return typeof raw === 'boolean' ? raw : null
}

/**
 * Whether a vocabulary this console owns really contains a word off the wire.
 *
 * `word in TABLE` walks the prototype chain, so `constructor`, `toString`,
 * `valueOf`, `hasOwnProperty` and `__proto__` all answer true against any
 * object literal — and the lookup that follows returns a FUNCTION, not a tone.
 * An unrecognised value would come back "recognised", chip itself with
 * `Object.prototype.toString` in place of a tone, and record no gap, so the
 * screen would badge itself live over a word it does not know.
 */
export function knows(table: Readonly<Record<string, unknown>>, word: string): boolean {
  return Object.prototype.hasOwnProperty.call(table, word)
}

// ---------------------------------------------------------------------------
// Numbers and instants
// ---------------------------------------------------------------------------

/**
 * An integer we can state exactly, or the reason we cannot.
 *
 * Three states, not two, because absent and unusable are different facts about
 * different bugs. An id past `Number.MAX_SAFE_INTEGER` has already lost digits
 * in `JSON.parse` — printing it shows an identifier wrong in its last places
 * and entirely plausible, which is how an operator opens the wrong record.
 */
export type IntRead =
  | { readonly kind: 'exact'; readonly value: number }
  | { readonly kind: 'absent' }
  | { readonly kind: 'unreadable' }

export function readInt(o: Record<string, unknown>, key: string): IntRead {
  const raw = o[key]
  if (raw === undefined || raw === null) return { kind: 'absent' }
  if (typeof raw !== 'number' || !Number.isSafeInteger(raw)) return { kind: 'unreadable' }
  return { kind: 'exact', value: raw }
}

/**
 * A duration in seconds. A negative one is unreadable rather than clamped.
 *
 * Clamping to zero is the tempting move and it is a fabricated zero: a negative
 * elapsed time means the clocks disagree, and "0s" tells the operator the thing
 * just happened. Two different incidents, one of them invisible.
 */
export function readDuration(o: Record<string, unknown>, key: string): IntRead {
  const read = readInt(o, key)
  return read.kind === 'exact' && read.value < 0 ? { kind: 'unreadable' } : read
}

export type InstantRead =
  | { readonly kind: 'at'; readonly label: string }
  | { readonly kind: 'absent' }
  | { readonly kind: 'unreadable' }

/**
 * An RFC-3339 instant, in the format the design draws timestamps in.
 *
 * The epoch guard is not paranoia. Several fields on this surface are plain
 * `time.Time` with no `omitempty`, so a zero value reaches the wire as
 * `0001-01-01T00:00:00Z` and parses perfectly well into a date in the year 1 —
 * which renders as a timestamp rather than as an absence.
 */
export function readInstant(o: Record<string, unknown>, key: string): InstantRead {
  const raw = o[key]
  if (raw === undefined || raw === null) return { kind: 'absent' }
  if (typeof raw !== 'string' || raw === '') return { kind: 'unreadable' }
  const at = new Date(raw)
  if (Number.isNaN(at.getTime()) || at.getTime() <= 0) return { kind: 'unreadable' }
  return { kind: 'at', label: readAtStamp(at) }
}

/** Just the day. A value date is a day, and the time on it is noise. */
export function readDay(o: Record<string, unknown>, key: string): string | null {
  const raw = o[key]
  if (typeof raw !== 'string' || raw === '') return null
  const at = new Date(raw)
  if (Number.isNaN(at.getTime()) || at.getTime() <= 0) return null
  return at.toISOString().slice(0, 10)
}

/**
 * A span, from the server's own arithmetic. No ambient clock here.
 *
 * Truncated, never rounded up, and minutes are kept until two days out — an
 * elapsed time displayed as "5h" when it is 4h 01m overstates it, and one
 * displayed as "4h" when it is 4h 59m understates it. Past two days the minutes
 * are noise and the days are what gets escalated.
 */
export function spanOf(seconds: number): string {
  const s = Math.trunc(seconds)
  if (s < 60) return `${String(s)}s`
  const minutes = Math.floor(s / 60)
  if (minutes < 60) return `${String(minutes)}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `${String(hours)}h ${String(minutes % 60).padStart(2, '0')}m`
  return `${String(Math.floor(hours / 24))}d ${String(hours % 24)}h`
}

// ---------------------------------------------------------------------------
// Money — law 8, in one place
// ---------------------------------------------------------------------------

/**
 * An amount cell, or a stated absence. Never a bare number.
 *
 * Both halves must read. A minor value with no currency is the one figure
 * somebody might quote to a customer or an institution, and a currency with no
 * amount is not an amount.
 *
 * `exactMinor` reads BOTH the quoted-decimal form `minorUnits` marshals and the
 * bare JSON integer other views emit — the encoding is not uniform across this
 * surface and its own spec description wrongly claims it is (go-wallet#859), so
 * every field is read rather than assumed — and it refuses anything past
 * 2^53−1, where `JSON.parse` has already dropped digits and no check downstream
 * can recover them.
 */
export function amountCell(minorRaw: unknown, currency: unknown): Cell {
  const minor = exactMinor(minorRaw)
  if (minor === null) return '— not a figure this console can state'
  if (!isCurrencyCode(currency)) return '— no currency on this amount'
  return [minor, currency.trim().toUpperCase()]
}

/** The `{amount_minor, currency}` pair, as a cell. */
export function moneyCell(o: Record<string, unknown>, key: string): Cell {
  const m = asRecord(o[key])
  if (m === null) return '— not stated'
  return amountCell(m['amount_minor'], m['currency'])
}

/** Whether a `moneyView` read cleanly, for gap accounting. */
export function moneyReadable(o: Record<string, unknown>, key: string): boolean {
  const m = asRecord(o[key])
  if (m === null) return false
  return exactMinor(m['amount_minor']) !== null && isCurrencyCode(m['currency'])
}

/** The signed minor value of a `moneyView`, for deciding a tone. Never rendered. */
export function moneyMinor(o: Record<string, unknown>, key: string): number | null {
  const m = asRecord(o[key])
  if (m === null) return null
  return exactMinor(m['amount_minor'])
}

// ---------------------------------------------------------------------------
// Gaps
// ---------------------------------------------------------------------------

/** Groups repeated gaps so the provenance chip stays readable at many rows. */
export function summarise(gaps: readonly string[]): string[] {
  const counts = new Map<string, number>()
  for (const gap of gaps) counts.set(gap, (counts.get(gap) ?? 0) + 1)
  return [...counts].map(([label, n]) =>
    `${label} (${String(n)} ${n === 1 ? 'row' : 'rows'})`)
}

// ---------------------------------------------------------------------------
// One read, resolved into an answer either way
// ---------------------------------------------------------------------------

export type Attempt =
  | { readonly ok: true; readonly payload: unknown }
  | {
      readonly ok: false
      readonly reason: string
      readonly decisionRef: string | null
      readonly refused: boolean
      /** Kept because some 404s are a STATE rather than a failure. */
      readonly status: number | null
    }

/**
 * An `AbortError` is rethrown: a navigation away is a cancellation, not a
 * degraded screen, and swallowing it here would render "this could not be read"
 * over a screen the operator has already left.
 */
export async function attempt(read: () => Promise<unknown>, subject: string): Promise<Attempt> {
  try {
    return { ok: true, payload: await read() }
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        ok: false,
        reason: `${subject} answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        decisionRef: error.decisionRef,
        refused: error.isForbidden,
        status: error.status,
      }
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return {
      ok: false, reason: `${subject} could not be reached.`,
      decisionRef: null, refused: false, status: null,
    }
  }
}

/** A failure this console produced itself, for a shape it did not recognise. */
export function malformed(reason: string): Extract<Attempt, { ok: false }> {
  return { ok: false, reason, decisionRef: null, refused: false, status: null }
}

/**
 * How a refusal is quoted, and what is said when it cannot be.
 *
 * Law 13 — every refusal surfaces a `decision_ref`. One that arrives without
 * one leaves an operator with nothing to put in an escalation, and the console
 * says exactly that rather than inventing a reference or staying quiet.
 */
export function refLine(failed: Extract<Attempt, { ok: false }>, fallback: string): string {
  if (failed.decisionRef !== null) return `decision_ref ${failed.decisionRef}`
  if (failed.refused) {
    return 'The refusal arrived without a decision_ref, so there is nothing to quote in an escalation.'
  }
  return fallback
}

/** The stamp, when the endpoint does not say when it read. */
export function receipt(at: Date): string {
  return `${readAtStamp(at)} (console receipt — the endpoint does not state when it read)`
}

// ---------------------------------------------------------------------------
// The backend's own words
// ---------------------------------------------------------------------------

/**
 * The longest body `withoutRoadmapNotes` lets through, less room for an
 * ellipsis. Kept here rather than imported from `./roadmap` so this module does
 * not depend on that filter's internals; the tests that fail if they diverge
 * are the ones asserting a disclosure survives a round trip through
 * `readScreen`.
 */
const MAX_DISCLOSURE = 210

/**
 * The backend's own disclosure sentence, as the backend wrote it.
 *
 * These responses carry required `*_note` fields written by the handler that
 * knows what its own schema cannot say, and they are quoted rather than
 * paraphrased because a paraphrase drifts in one direction: it turns a
 * permanent property of the system into something that reads as a temporary
 * gap.
 *
 * They cannot be rendered whole. `withoutRoadmapNotes` drops any note body over
 * 220 characters at `readScreen` (#120), and every one of these fields is
 * several times that — so a verbatim block renders NOTHING, which is worse than
 * an extract because the operator is then told nothing at all. `BlockHead.note`
 * is a one-line right-aligned strip and `foot2` is stripped by the same rule,
 * so the block language has no home for a paragraph either.
 *
 * What is rendered is the field's own FIRST SENTENCE, verbatim, with the field
 * named in `ref`. These handlers front-load the load-bearing clause, so the
 * first sentence is the one that changes what somebody does.
 *
 * Nothing is emitted when the backend did not send the field: this console must
 * never state a sentence of its own as the backend's.
 */
export function disclosure(
  o: Record<string, unknown>, key: string, kicker: string, title: string, tone: Tone,
): NoteBlock | null {
  const full = readText(o, key)
  if (full === null) return null
  const match = /^[\s\S]*?[.](?=\s|$)/.exec(full.trim())
  let body = (match === null ? full.trim() : match[0]).trim()
  if (body.length > MAX_DISCLOSURE) {
    const cut = body.slice(0, MAX_DISCLOSURE).lastIndexOf(' ')
    body = `${body.slice(0, cut > 0 ? cut : MAX_DISCLOSURE).trimEnd()}…`
  }
  return { t: 'note', tone, kicker, title, body, ref: `${key}, as the backend wrote it` }
}
